import { useAuthContext } from "Data/auth/hooks"
import { LINKS } from "Tools/navigation"
import { PERMISSIONS } from "Tools/permissions"
import { useMemo } from "react"


export const getFullName = ({ first_name = '', last_name = '', email }: User = {} as User) => first_name && `${first_name} ${last_name}` || email || ''


export const getNameInitials = ({ first_name = '', last_name = '' }: User = {} as User) => first_name[0] + last_name[0]

export const hasPermissions = (user = null as User | null | undefined, permissions: (string | string[])[] = []) => {
    if (!user) return false
    // if (user.is_system_admin) return true
    return permissions.length ? permissions.flat().every(p => user.user_permissions.includes(p)) : true
}

export const hasSyngentaEmail = (user?: User | null) => /\@syngenta\.com$/i.test((user?.email || '').trim())

export const usePermissions = (permissions: (string | string[])[] = []) => {
    let { user } = useAuthContext()
    let has = useMemo(() => hasPermissions(user, permissions), [user])
    return has
}
export const useAnyPermissions = (permissions: (string | string[])[] = []) => {
    let { user } = useAuthContext()
    let has = useMemo(() => hasAnyPermissions(user!, permissions), [user])
    return has
}

export const useAdminPermissions = () => {
    let isAdmin = useAnyPermissions([
        ...PERMISSIONS.user.ADMIN_ACTIVITY, ...PERMISSIONS.user.MANAGE_FROST, ...PERMISSIONS.user.ADMIN_RESISTANCE_SAMPLING,
        ...PERMISSIONS.user.ADMIN_SEEDCARE, ...PERMISSIONS.user.ADMIN_EQUIPMENT,
    ])
    return isAdmin
}
export const useGenericPermission = (module: 'Seedcare'|'Data Quality' | 'FROST' | 'Sampling' | 'Digital Equipment' | 'Activity Planner') => {
    const { user: u } = PERMISSIONS
    const { user } = useAuthContext()
    switch (module) {
        case 'Seedcare':
            return hasPermissions(user!, u.SEEDCARE_GENERIC) //&& !hasAnyPermissions(user!, [u.RTM, u.SEEDCARE_COUNTRY_LEAD, u.FS, u.ADMIN_SEEDCARE, u.CRO, u.DLO, u.STF, u.SEEDCARE_RTM_SUPPORT])
        case 'Sampling':
            return hasPermissions(user!, u.RESISTANCE_SAMPLING_GENERIC) //&& !hasAnyPermissions(user!, [u.RTM,u.RESISTANCE_SAMPLING_LAB_FS,u.SYSTEM, u.FS, u.DLO, u.ADMIN_RESISTANCE_SAMPLING, u.CRO])
        case 'Digital Equipment':
            return hasPermissions(user!, u.EQUIPMENT_GENERIC) //&& !hasAnyPermissions(user!, [u.ADMIN_EQUIPMENT, u.FS, u.EQUIPMENT_COUNTRY_ADMIN])
        case 'FROST':
            return hasPermissions(user!, u.FROST_GENERIC) //&& !hasAnyPermissions(user!, [u.ADMIN_FROST, u.FS, u.RTM, u.DLO, u.CRO, u.FROST_FS_COUNTRY_LEAD])
        case 'Activity Planner':
            return hasPermissions(user!, u.ACTIVITY_GENERIC) && !hasAnyPermissions(user!, [u.ADMIN_ACTIVITY, u.FS, u.ACTIVITY_COUNTRY_LEAD])
        case 'Data Quality':
            return hasPermissions(user!, u.DATA_QUALITY_GENERIC) //&& !hasAnyPermissions(user!, [u.ADMIN_DATA_QUALITY,  u.DATA_QUALITY_MANAGER])
        default:
            throw Error('Not implemented')
    }
}
export const useModuleAdminPermission = (module: 'Seedcare' | 'Data Quality' | 'FROST' | 'Sampling' | 'Digital Equipment' | 'Activity Planner') => {
    const { user: u } = PERMISSIONS
    const { user } = useAuthContext()
    switch (module) {
        case 'Seedcare':
            return !hasPermissions(user!, u.SEEDCARE_GENERIC) && hasPermissions(user!, u.ADMIN_SEEDCARE) //&& !hasAnyPermissions(user!, [u.RTM, u.SEEDCARE_COUNTRY_LEAD, u.FS, u.ADMIN_SEEDCARE, u.CRO, u.DLO, u.STF, u.SEEDCARE_RTM_SUPPORT])
        case 'Sampling':
            return !hasPermissions(user!, u.RESISTANCE_SAMPLING_GENERIC) && hasPermissions(user!, u.ADMIN_RESISTANCE_SAMPLING)  //&& !hasAnyPermissions(user!, [u.RTM,u.RESISTANCE_SAMPLING_LAB_FS,u.SYSTEM, u.FS, u.DLO, u.ADMIN_RESISTANCE_SAMPLING, u.CRO])
        case 'Digital Equipment':
            return !hasPermissions(user!, u.EQUIPMENT_GENERIC) && hasPermissions(user!, u.ADMIN_EQUIPMENT)  //&& !hasAnyPermissions(user!, [u.ADMIN_EQUIPMENT, u.FS, u.EQUIPMENT_COUNTRY_ADMIN])
        case 'FROST':
            return !hasPermissions(user!, u.FROST_GENERIC) && hasPermissions(user!, u.MANAGE_FROST) //&& !hasAnyPermissions(user!, [u.ADMIN_FROST, u.FS, u.RTM, u.DLO, u.CRO, u.FROST_FS_COUNTRY_LEAD])
        case 'Activity Planner':
            return !hasPermissions(user!, u.ACTIVITY_GENERIC) && hasPermissions(user!, u.ADMIN_ACTIVITY) //&& !hasAnyPermissions(user!, [u.ADMIN_ACTIVITY, u.FS, u.ACTIVITY_COUNTRY_LEAD])
        case 'Data Quality':
            return !hasPermissions(user!, u.DATA_QUALITY_GENERIC) && hasPermissions(user!, u.ADMIN_DATA_QUALITY)  //&& !hasAnyPermissions(user!, [u.ADMIN_DATA_QUALITY,  u.DATA_QUALITY_MANAGER])
        default:
            throw Error('Not implemented')
    }
}
export const hasAnyPermissions = (user = {} as User|null, permissions: (string | string[])[] = []) => {
    // System admins have all permissions
    // if (user.is_system_admin) return true
    return permissions.length ? permissions.flat().some(p => user?.user_permissions.includes(p)) : true
}

/**
 * Module names mapping to their admin permissions
 */ 
const MODULE_ADMIN_PERMISSIONS: Record<string, string> = {
    'FROST': PERMISSIONS.user.MANAGE_FROST[0],
    'Seedcare': PERMISSIONS.user.ADMIN_SEEDCARE[0],
    'Sampling': PERMISSIONS.user.ADMIN_RESISTANCE_SAMPLING[0],
    'Equipment Stock': PERMISSIONS.user.ADMIN_EQUIPMENT[0],
    'Data Quality': PERMISSIONS.user.ADMIN_DATA_QUALITY[0],
    'Activity Center': PERMISSIONS.user.ADMIN_ACTIVITY[0],
    'Chemical inventory': PERMISSIONS.user.MANAGE_CHEMICALS[0],
    'Soil Sensor': PERMISSIONS.user.ADMIN_SOIL_SENSOR[0],
}

export const getUserAdminModules = (user: User | null): string[] => {
    if (!user) return []
    if (user.is_system_admin) return Object.keys(MODULE_ADMIN_PERMISSIONS)

    const adminModules: string[] = []

    Object.entries(MODULE_ADMIN_PERMISSIONS).forEach(([module, permission]) => {
        if (user.user_permissions.includes(permission)) {
            adminModules.push(module)
        }
    })

    return adminModules
}

// export const canEditRolesAndPermissions = (user: User | null): boolean => {
//     if (!user) return false
//     if (user.is_system_admin) return true

//     // return getUserAdminModules(user).length > 0
// }

export const filterRolesByModules = (
    roles: Record<string, { name: string; code: string; permissions: string[] }>,
    permissions: RolePermission[],
    allowedModules: string[]
): Record<string, { name: string; code: string; permissions: string[] }> => {

    if (allowedModules.length === Object.keys(MODULE_ADMIN_PERMISSIONS).length) {
        return roles
    }

    const permissionModuleMap = new Map<string, string>()
    permissions.forEach(perm => {
        const effectiveModule = (perm.module === 'Accounts' && perm.target_module)
            ? perm.target_module
            : perm.module
        permissionModuleMap.set(perm.code, effectiveModule)
    })

    const filteredRoles: Record<string, { name: string; code: string; permissions: string[] }> = {}
    const roleAnalysis: any[] = []

    Object.entries(roles).forEach(([roleName, roleData]) => {
        if (roleName === 'System Admin') {
            return
        }

        const roleModules = new Set<string>()

        roleData.permissions.forEach(permCode => {
            const module = permissionModuleMap.get(permCode)
            if (module) {
                roleModules.add(module)
            }
        })

        const realModules = Array.from(roleModules).filter(m => m !== 'Generic' && m !== 'Admin' && m !== 'General')

        const substantivePermCount = new Map<string, number>()
        roleData.permissions.forEach(permCode => {
            const module = permissionModuleMap.get(permCode)
            const isAddressbookView = permCode.startsWith('view_addressbook_') && !permCode.includes('edit')
            if (module && module !== 'Generic' && module !== 'Admin' && module !== 'General' && !isAddressbookView) {
                substantivePermCount.set(module, (substantivePermCount.get(module) || 0) + 1)
            }
        })

        const modulePermCount = new Map<string, number>()
        roleData.permissions.forEach(permCode => {
            const module = permissionModuleMap.get(permCode)
            if (module && module !== 'Generic' && module !== 'Admin' && module !== 'General') {
                modulePermCount.set(module, (modulePermCount.get(module) || 0) + 1)
            }
        })

        let primaryModule: string | null = null
        let maxCount = 0
        modulePermCount.forEach((count, module) => {
            if (count > maxCount) {
                maxCount = count
                primaryModule = module
            }
        })

        const substantiveModules = Array.from(substantivePermCount.keys())

        const isGenericOnly = roleModules.size > 0 && realModules.length === 0
        const hasSubstantiveAllowedModule = substantiveModules.some(m => allowedModules.includes(m))
        const shouldInclude = isGenericOnly || hasSubstantiveAllowedModule || roleModules.size === 0

        if (roleName.toLowerCase().includes('seedcare') || roleName.toLowerCase().includes('admin') || roleName.toLowerCase().includes('field')) {
            roleAnalysis.push({
                roleName,
                allModules: Array.from(roleModules),
                realModules,
                substantiveModules,
                primaryModule,
                primaryModuleCount: maxCount,
                isGenericOnly,
                hasSubstantiveAllowedModule,
                shouldInclude,
                permissionsCount: roleData.permissions.length,
                substantivePermCounts: Object.fromEntries(substantivePermCount)
            })
        }

        if (shouldInclude) {
            filteredRoles[roleName] = roleData
        }
    })

    // console.log('filterRolesByModules - All roles with their modules:',
    Object.entries(filteredRoles).map(([name, data]) => {
        const roleModules = new Set<string>()
        data.permissions.forEach(p => {
            const m = permissionModuleMap.get(p)
            if (m) roleModules.add(m)
        })
        const realModules = Array.from(roleModules).filter(m => m !== 'Generic' && m !== 'Admin' && m !== 'General')

        const modulePermCount = new Map<string, number>()
        data.permissions.forEach(permCode => {
            const module = permissionModuleMap.get(permCode)
            if (module && module !== 'Generic' && module !== 'Admin' && module !== 'General') {
                modulePermCount.set(module, (modulePermCount.get(module) || 0) + 1)
            }
        })
        let primaryModule: string | null = null
        let maxCount = 0
        modulePermCount.forEach((count, module) => {
            if (count > maxCount) {
                maxCount = count
                primaryModule = module
            }
        })

        return { name, allModules: Array.from(roleModules), realModules, primaryModule, primaryModuleCount: maxCount }
    })
    // )

    return filteredRoles
}

export const filterPermissionsByModules = (
    permissions: RolePermission[],
    allowedModules: string[]
): RolePermission[] => {
    if (allowedModules.length === Object.keys(MODULE_ADMIN_PERMISSIONS).length) {
        return permissions
    }

    const filtered = permissions.filter(perm => {

        const effectiveModule = (perm.module === 'Accounts' && perm.target_module)
            ? perm.target_module
            : perm.module

        const shouldInclude = (
            allowedModules.includes(effectiveModule) ||
            perm.module === 'Generic' ||
            perm.module === 'General'
        )
        return shouldInclude
    })


    return filtered
}

/** Django group names that grant Soil Sensors module access (see backend shared_tools values / SoilSensorAppAccess). */
const SOIL_SENSOR_GROUP_NAMES = new Set([
    'Soil Sensor Admin',
    'Soil Sensor User',
    'Soil Sensor Country Admin',
    'Soil Sensor Generic',
    'Soil Sensor Provider',
])

const SOIL_SENSOR_PERMISSION_KEYS = PERMISSIONS.SoilSensor.ANY_PERMISSIONS

/**
 * Whether the user should see the Soil Sensors nav item and top-level routes.
 * Matches backend gate: groups and/or auth soil-sensor permissions from the permission matrix.
 */
export const canAccessSoilSensorApp = (user?: User | null): boolean => {
    if (!user) return false
    if (user.is_system_admin) return true
    if (user.groups?.some(g => SOIL_SENSOR_GROUP_NAMES.has(g.name))) return true
    return hasAnyPermissions(user, [...SOIL_SENSOR_PERMISSION_KEYS])
}

/** Permissions aligned with Soil Sensor Country Admin, Field Scientists, CRO managers (`manage_cros`), and CROs (`act_as_cro`). */
const SOIL_SENSOR_CREATE_ORDER_PERMISSIONS = PERMISSIONS.SoilSensor.CreateOrder.ANY_PERMISSIONS

/** Django groups that imply Create Order access (when JWT may not list individual frost_index codenames). */
const SOIL_SENSOR_CREATE_ORDER_GROUP_NAMES = new Set(['Soil Sensor Country Admin', 'Soil Sensor Admin'])

/**
 * Soil Sensors → Create Order: users who place orders from published protocol requests (not only protocol owners).
 */
export const canAccessSoilSensorCreateOrder = (user?: User | null): boolean => {
    if (!user) return false
    if (user.is_system_admin) return true
    if (user.groups?.some((g) => SOIL_SENSOR_CREATE_ORDER_GROUP_NAMES.has(g.name))) return true
    return hasAnyPermissions(user, [...SOIL_SENSOR_CREATE_ORDER_PERMISSIONS])
}

/** Shipments & returns: same roles as Create Order (country admin, FS, CRO managers, CROs). */
export const canAccessSoilSensorShipping = canAccessSoilSensorCreateOrder

/** Django group for Soil Sensors general administrators (catalog / dictionary management). */
const SOIL_SENSOR_ADMIN_GROUP_NAMES = new Set(['Soil Sensor Admin'])

/**
 * Admin Sensors page (eligible manufacturer/model management). General admins, users with
 * `auth.add_soil_sensor`, or system admins.
 */
export const canAccessSoilSensorAdminSensors = (user?: User | null): boolean => {
    if (!user) return false
    if (user.is_system_admin) return true
    if (user.groups?.some((g) => SOIL_SENSOR_ADMIN_GROUP_NAMES.has(g.name))) return true
    return hasAnyPermissions(user, PERMISSIONS.SoilSensor.AdminSensors.ANY_PERMISSIONS)
}

const SOIL_SENSOR_SENSORS_CATALOG_GROUP_NAMES = new Set(['Soil Sensor Admin'])

/**
 * Sensors catalog (Equipment Stock → Sensor Request dropdown). General admins only:
 * `auth.administrate_soil_sensor_app` / `auth.add_soil_sensor`, or Soil Sensor Admin group.
 */
export const canAccessSoilSensorSensorsCatalog = (user?: User | null): boolean => {
    if (!user) return false
    if (user.is_system_admin) return true
    if (user.groups?.some((g) => SOIL_SENSOR_SENSORS_CATALOG_GROUP_NAMES.has(g.name))) return true
    return hasAnyPermissions(user, PERMISSIONS.SoilSensor.SensorsCatalog.ANY_PERMISSIONS)
}

/** Soil Sensors → Providers submodule (external sensor providers + general admins). */
const SOIL_SENSOR_PROVIDER_ROLE_GROUP_NAMES = new Set([
    'Soil Sensor Provider',
])

export const canAccessSoilSensorProvidersPage = (user?: User | null): boolean => {
    if (!user) return false
    if (user.is_system_admin) return true
    if (user.groups?.some((g) => SOIL_SENSOR_PROVIDER_ROLE_GROUP_NAMES.has(g.name))) return true
    if (user.groups?.some((g) => SOIL_SENSOR_SENSORS_CATALOG_GROUP_NAMES.has(g.name))) return true
    return hasAnyPermissions(user, PERMISSIONS.SoilSensor.Providers.ANY_PERMISSIONS)
}

/** Default landing route under Soil Sensors for the current user. */
export const soilSensorDefaultPath = (user?: User | null): string => {
    if (!user) return LINKS.soilSensorProtocolOwner
    if (canAccessSoilSensorProvidersPage(user)) {
        return LINKS.soilSensorProviders
    }
    return LINKS.soilSensorProtocolOwner
}